import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, Typography, Space } from "antd";
import {
	VideoCameraOutlined,
	CarOutlined,
	CalendarOutlined,
	UnorderedListOutlined,
	FormOutlined,
	IdcardOutlined,
	ClockCircleOutlined,
	LogoutOutlined,
	SearchOutlined,
} from "@ant-design/icons";
import {
	isTokenValid,
	hasStoredSession,
	logoutFromServer,
	getCurrentUserProfile,
	setStoredUserClassificationName,
	USER_CLASSIFICATION_NAME_KEY,
} from "../utilities/AuthService";
import { unregisterFcmToken } from "../utilities/firebaseMessaging";
import useAxios from "../utilities/useAxios";

const { Title } = Typography;

const Home = () => {
	const navigate = useNavigate();
	const [isOfficeUser, setIsOfficeUser] = useState(false);
	const [isOfficeSanctuaryUser, setIsOfficeSanctuaryUser] =
		useState(false);
	const [isOfficeLeaveUser, setIsOfficeLeaveUser] = useState(false);
	const [canViewMonthlyAttendance, setCanViewMonthlyAttendance] =
		useState(false);
	const [canApproveLeave, setCanApproveLeave] = useState(false);
	const [isCashDisbursementReviewer, setIsCashDisbursementReviewer] =
		useState(false);
	const [isAccountingUser, setIsAccountingUser] = useState(false);
	const [classificationName, setClassificationName] = useState(null);
	const [classificationReady, setClassificationReady] = useState(false);

	useEffect(() => {
		if (!isTokenValid()) {
			setIsOfficeUser(false);
			setIsOfficeSanctuaryUser(false);
			setIsOfficeLeaveUser(false);
			setCanViewMonthlyAttendance(false);
			setIsCashDisbursementReviewer(false);
			setIsAccountingUser(false);
			setClassificationName(null);
			setClassificationReady(false);
			return;
		}

		const groupStr = localStorage.getItem("user_groups");
		if (groupStr) {
			try {
				const groups = JSON.parse(groupStr);
				setIsOfficeUser(groups.includes("office"));
				setIsOfficeSanctuaryUser(
					groups.includes("office_sanctuary"),
				);
				setIsOfficeLeaveUser(
					groups.includes("office_leave"),
				);
				setCanViewMonthlyAttendance(
					!groups.includes("office_leave") &&
						(groups.includes(
							"office_director",
						) ||
							groups.includes(
								"church_leader",
							)),
				);
				setIsCashDisbursementReviewer(
					groups.includes("office_director") ||
						groups.includes(
							"office_deputy-director",
						),
				);
				setIsAccountingUser(
					groups.includes("accounting"),
				);
			} catch (error) {
				console.error(
					"Error parsing user groups:",
					error,
				);
			}
		} else {
			setIsOfficeUser(false);
			setIsOfficeSanctuaryUser(false);
			setIsOfficeLeaveUser(false);
			setCanViewMonthlyAttendance(false);
			setIsCashDisbursementReviewer(false);
			setIsAccountingUser(false);
		}

		const cached = localStorage.getItem(
			USER_CLASSIFICATION_NAME_KEY,
		);
		if (cached !== null) {
			setClassificationName(cached);
			setClassificationReady(true);
			return;
		}

		let cancelled = false;
		(async () => {
			try {
				const profile = await getCurrentUserProfile();
				const name = profile.classification_name ?? "";
				setStoredUserClassificationName(name);
				if (!cancelled) {
					setClassificationName(name);
				}
			} catch (error) {
				console.error(
					"Error loading user profile:",
					error,
				);
				if (!cancelled) {
					setClassificationName("");
				}
			} finally {
				if (!cancelled) {
					setClassificationReady(true);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		if (!isTokenValid()) {
			setCanApproveLeave(false);
			return;
		}
		let cancelled = false;
		(async () => {
			try {
				const res = await useAxios.get(
					"/utils/leave/approvals/capability/",
				);
				if (!cancelled) {
					setCanApproveLeave(
						Boolean(
							res.data
								?.can_approve_leave,
						),
					);
				}
			} catch (e) {
				if (!cancelled) {
					setCanApproveLeave(false);
				}
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	const isClergyOrStaff =
		classificationName === "교역자" ||
		classificationName === "교직원";
	const showMemberPortal = classificationReady;
	const showLeaveApply = classificationReady && isClergyOrStaff;
	const showCashDispStaffActions = classificationReady && isClergyOrStaff;
	const showRentalSanctuary = !isOfficeUser;
	const showSanctuaryLookup =
		!isTokenValid() || !isOfficeSanctuaryUser;
	const isLoggedIn = hasStoredSession();

	const handleLogout = async () => {
		try {
			await unregisterFcmToken();
		} catch (error) {
			console.error("FCM token unregister failed:", error);
		}
		await logoutFromServer();
		navigate("/login", { replace: true });
	};

	const handleNavigateToCma = () => {
		navigate("/cma");
	};

	const handleNavigateToAccessedCars = () => {
		navigate("/utils/accessed-car/view-accessed-cars");
	};

	const handleNavigateToRentalSanctuary = () => {
		navigate(
			"/utils/rent-sanctuary/view-rental-sanctuary-schedule",
		);
	};

	const handleNavigateToBookSanctuary = () => {
		navigate("/utils/rent-sanctuary/book-sanctuary");
	};

	const handleNavigateToLookupSanctuary = () => {
		navigate("/utils/rent-sanctuary/lookup-applications");
	};

	const handleNavigateToRentalCars = () => {
		navigate("/utils/rent-cars/");
	};

	const handleNavigateToRentalCarApply = () => {
		navigate("/utils/rent-cars/apply/");
	};

	const handleNavigateToLeaveApplications = () => {
		navigate("/utils/leave/view-applications/");
	};

	const handleNavigateToEmployeeInfo = () => {
		navigate("/member/manage-employee-info");
	};

	const handleNavigateToAttendance = () => {
		navigate("/attendance/monthly");
	};

	const handleNavigateToAttendanceView = () => {
		navigate("/attendance-view/monthly");
	};

	const handleNavigateToApplyForLeave = () => {
		navigate("/utils/leave/my-status/");
	};

	const handleNavigateToLeaveApprovals = () => {
		navigate("/utils/leave/approvals/");
	};

	const handleNavigateToCashDispCreate = () => {
		navigate("/utils/cash-disbursement/create/");
	};

	const handleNavigateToCashDispMyList = () => {
		navigate("/utils/cash-disbursement/my-list/");
	};

	const handleNavigateToCashDispOfficeReview = () => {
		navigate("/utils/cash-disbursement/office-review/");
	};

	const handleNavigateToCashDispAccounting = () => {
		navigate("/utils/cash-disbursement/accounting/");
	};

	return (
		<div className="home-portal">
			<Card className="home-portal__card">
				<Space
					direction="vertical"
					size="large"
					style={{ width: "100%" }}>
					<Title level={2}>
						평강제일교회 포털
					</Title>

					{showMemberPortal && (
						<>
							<Button
								type="primary"
								size="large"
								icon={
									<VideoCameraOutlined />
								}
								onClick={
									handleNavigateToCma
								}
								style={{
									width: "100%",
								}}>
								기독사관학교
								강의실로 이동
							</Button>
						</>
					)}
					{isOfficeUser && (
						<Button
							type="default"
							size="large"
							icon={<CarOutlined />}
							onClick={
								handleNavigateToAccessedCars
							}
							style={{
								width: "100%",
							}}>
							차량 관리
						</Button>
					)}
					{(isOfficeUser ||
						isOfficeSanctuaryUser) && (
						<Button
							type="default"
							size="large"
							icon={
								<CalendarOutlined />
							}
							onClick={
								handleNavigateToRentalSanctuary
							}
							style={{
								width: "100%",
							}}>
							성전 대여 관리
						</Button>
					)}

					{showLeaveApply && (
						<Button
							type="default"
							size="large"
							icon={
								<CalendarOutlined />
							}
							onClick={
								handleNavigateToApplyForLeave
							}
							style={{
								width: "100%",
							}}>
							근태 현황 및 신청
						</Button>
					)}

					{canApproveLeave && (
						<Button
							type="default"
							size="large"
							icon={
								<UnorderedListOutlined />
							}
							onClick={
								handleNavigateToLeaveApprovals
							}
							style={{
								width: "100%",
							}}>
							연차 승인
						</Button>
					)}

					{/* {showCashDispStaffActions && (
						<>
							<Button
								type="default"
								size="large"
								icon={
									<FormOutlined />
								}
								onClick={
									handleNavigateToCashDispCreate
								}
								style={{
									width: "100%",
								}}>
								지출결의서
							</Button>
						</>
					)} */}

					{isCashDisbursementReviewer && (
						<Button
							type="default"
							size="large"
							icon={
								<UnorderedListOutlined />
							}
							onClick={
								handleNavigateToCashDispOfficeReview
							}
							style={{
								width: "100%",
							}}>
							지출결의서 조회 · 결재
						</Button>
					)}

					{isAccountingUser && (
						<Button
							type="default"
							size="large"
							icon={
								<UnorderedListOutlined />
							}
							onClick={
								handleNavigateToCashDispAccounting
							}
							style={{
								width: "100%",
							}}>
							지출결의서(회계) 승인 건
						</Button>
					)}

					{isOfficeLeaveUser && (
						<>
							<Button
								type="default"
								size="large"
								icon={
									<ClockCircleOutlined />
								}
								onClick={
									handleNavigateToAttendance
								}
								style={{
									width: "100%",
								}}>
								교직원 근태 관리
							</Button>
						</>
					)}
					{canViewMonthlyAttendance && (
						<Button
							type="default"
							size="large"
							icon={
								<ClockCircleOutlined />
							}
							onClick={
								handleNavigateToAttendanceView
							}
							style={{
								width: "100%",
							}}>
							월별 출근 현황
						</Button>
					)}
					{showRentalSanctuary && (
						<>
							<Button
								type="default"
								size="large"
								icon={
									<CalendarOutlined />
								}
								onClick={
									handleNavigateToBookSanctuary
								}
								style={{
									width: "100%",
								}}>
								성전 대여 신청
							</Button>
							{/* <Button
								type="default"
								size="large"
								icon={
									<CarOutlined />
								}
								onClick={
									handleNavigateToRentalCarApply
								}
								style={{
									width: "100%",
								}}>
								차량 대여 신청
							</Button> */}
						</>
					)}
					{showSanctuaryLookup && (
						<Button
							type="default"
							size="large"
							icon={<SearchOutlined />}
							onClick={
								handleNavigateToLookupSanctuary
							}
							style={{
								width: "100%",
							}}>
							성전 대여 신청 조회
						</Button>
					)}
				</Space>
			</Card>
			{isLoggedIn && (
				<Button
					type="primary"
					danger
					size="large"
					icon={<LogoutOutlined />}
					onClick={handleLogout}
					style={{
						width: "100%",
						maxWidth: "500px",
					}}>
					로그아웃
				</Button>
			)}
		</div>
	);
};

export default Home;
